import * as React from 'react';
import { connect } from "react-redux";
import { Dispatch } from "redux";
import { Action, AdditionalCriteriaType } from 'app/pages/DiscountEditor/actionTypes';
import ComponentHeader from 'app/shared/ui/ComponentHeader';
import { Dropdown, Button, Tooltip, Alert } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { setAddOrEditCriteriaModal, setDeleteCriteriaConfirmationModal } from 'app/pages/DiscountEditor/actions';
import { StudyStartDateCriteria, getStudyStartDateCriteria, BookingSystemCriteria, EmptyCriteria, getBookingSystemCriteria, getAdditionalProductFilterByType } from 'app/pages/DiscountEditor/modules/AdditionalCriteria';
import StudyStartDateCriteriaDisplay from 'app/pages/DiscountEditor/components/AdditionalCriteriaEditor/StudyStartDateCriteriaDisplay';
import BookingSystemCriteriaDisplay from 'app/pages/DiscountEditor/components/AdditionalCriteriaEditor/BookingSystemCriteriaDisplay';
import ProductCriteriaDisplay from 'app/pages/DiscountEditor/components/AdditionalCriteriaEditor/ProductCriteriaDisplay';
import ExtraProductCriteriaModal from 'app/pages/DiscountEditor/components/AdditionalCriteriaEditor/ExtraProductCriteriaModal';
import { ProductFilterSection } from 'app/pages/DiscountEditor/modules/AdditionalCriteria/state';
import DeleteCriteriaConfirmationModal from 'app/pages/DiscountEditor/components/AdditionalCriteriaEditor/DeleteCriteriaConfirmationModal';
import { canEditApplicableProducts, getSelectedSchools } from 'app/pages/DiscountEditor/modules/ProductSelection';
import { getSelectedDivisionIds } from 'app/pages/DiscountEditor/modules/Divisions/selectors';
import ErrorSummary from 'app/pages/DiscountEditor/components/ErrorSummary';
import { getSectionError } from 'app/pages/DiscountEditor/modules/Editor';
import { SaveDiscountErrorCategory } from 'app/shared/model/SaveDiscountResultDto';

interface StateProps {
  canEditCriteria: boolean;
  canEditProductCriteria: boolean;
  errorMessage?: string;
  studyStartDateCriteria: StudyStartDateCriteria;
  bookingSystemCriteria: BookingSystemCriteria;
  courseCriteria: ProductFilterSection | EmptyCriteria;
  accommodationCriteria: ProductFilterSection | EmptyCriteria;
  addOnCriteria: ProductFilterSection | EmptyCriteria;
  transferCriteria: ProductFilterSection | EmptyCriteria;
}

interface DispatchProps {
  onEdit: (criteriaType: AdditionalCriteriaType) => void;
  onDelete: (criteriaType: AdditionalCriteriaType) => void;
}

type InternalProps = StateProps & DispatchProps;

const AdditionalCriteriaEditor: React.FC<InternalProps> = props => (
  <div style={{ marginBottom: 16, background: '#fff', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
    <ComponentHeader text="Additional criteria" />
  <div style={{ padding: '0 16px 16px' }}>
    <ErrorSummary errorMessage={props.errorMessage} />
    <DeleteCriteriaConfirmationModal />
    <ExtraProductCriteriaModal />

    <StudyStartDateCriteriaDisplay
      criteria={props.studyStartDateCriteria}
      edit={() => props.onEdit(AdditionalCriteriaType.StudyStartDate)}
      delete={() => props.onDelete(AdditionalCriteriaType.StudyStartDate)}
    />

    <BookingSystemCriteriaDisplay
      criteria={props.bookingSystemCriteria}
      onEdit={() => props.onEdit(AdditionalCriteriaType.BookingSystem)}
      onDelete={() => props.onDelete(AdditionalCriteriaType.BookingSystem)}
    />

    {!props.canEditProductCriteria && (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        title="Select a division, at least one school, and dates"
        description="Additional course, accommodation, add-on, and transfer criteria are available after a division, at least one school, and dates in the Dates section are selected."
      />
    )}
    <div
      aria-disabled={!props.canEditProductCriteria}
      style={{
        opacity: props.canEditProductCriteria ? 1 : 0.45,
        pointerEvents: props.canEditProductCriteria ? 'auto' : 'none',
        userSelect: props.canEditProductCriteria ? 'auto' : 'none',
      }}
    >
      <ProductCriteriaDisplay
        courseCriteria={props.courseCriteria}
        accommodationCriteria={props.accommodationCriteria}
        transferCriteria={props.transferCriteria}
        addOnCriteria={props.addOnCriteria}
        onEdit={props.onEdit}
        onDelete={props.onDelete}
      />
    </div>

      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <AddCriteriaButton {...props} />
      </div>
    </div>
  </div>
);

function AddCriteriaButton(props: InternalProps) {
    const menuItems = [
      {
        key: 'studyStartDate',
        label: 'Study start date',
        disabled: props.studyStartDateCriteria.hasCriteria || !props.canEditCriteria,
        onClick: () => props.onEdit(AdditionalCriteriaType.StudyStartDate),
      },
      {
        key: 'bookingSystem',
        label: 'Booking system',
        disabled: props.bookingSystemCriteria.hasCriteria || !props.canEditCriteria,
        onClick: () => props.onEdit(AdditionalCriteriaType.BookingSystem),
      },
      { type: 'divider' as const },
      {
        key: 'course',
        label: 'Course',
        disabled: props.courseCriteria.hasCriteria || !props.canEditProductCriteria,
        onClick: () => props.onEdit(AdditionalCriteriaType.Course),
      },
      {
        key: 'accommodation',
        label: 'Accommodation',
        disabled: props.accommodationCriteria.hasCriteria || !props.canEditProductCriteria,
        onClick: () => props.onEdit(AdditionalCriteriaType.Accommodation),
      },
      {
        key: 'addOn',
        label: 'Add-on',
        disabled: props.addOnCriteria.hasCriteria || !props.canEditProductCriteria,
        onClick: () => props.onEdit(AdditionalCriteriaType.AddOns),
      },
      {
        key: 'transfer',
        label: 'Transfer',
        disabled: props.transferCriteria.hasCriteria || !props.canEditProductCriteria,
        onClick: () => props.onEdit(AdditionalCriteriaType.Transfer),
      },
    ];
  
    return (
      <div>
    <Tooltip title={!props.canEditCriteria ? 'Please select a division and at least one school' : ''}>
      <span>
        <Dropdown menu={{ items: menuItems }} disabled={!props.canEditCriteria} trigger={['click']}>
          <Button icon={<PlusOutlined />} type="primary">
            Add criteria
          </Button>
        </Dropdown>
      </span>
    </Tooltip>
      </div>
    );
  }
  

const mapStateToProps = (state: any): StateProps => {
  const hasSchools = Object.keys(getSelectedSchools(state)).length > 0;
  const hasDivision = getSelectedDivisionIds(state).length > 0;
  return {
    canEditCriteria: hasDivision && hasSchools,
    canEditProductCriteria: canEditApplicableProducts(state),
    errorMessage: getSectionError(state, SaveDiscountErrorCategory.AdditionalCriteria),
    studyStartDateCriteria: getStudyStartDateCriteria(state),
    bookingSystemCriteria: getBookingSystemCriteria(state),
    courseCriteria: getAdditionalProductFilterByType(state, AdditionalCriteriaType.Course),
    accommodationCriteria: getAdditionalProductFilterByType(state, AdditionalCriteriaType.Accommodation),
    addOnCriteria: getAdditionalProductFilterByType(state, AdditionalCriteriaType.AddOns),
    transferCriteria: getAdditionalProductFilterByType(state, AdditionalCriteriaType.Transfer),
  };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>): DispatchProps => ({
  onEdit: criteriaType => dispatch(setAddOrEditCriteriaModal(criteriaType)),
  onDelete: criteriaType => dispatch(setDeleteCriteriaConfirmationModal(criteriaType)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AdditionalCriteriaEditor);
